import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, Users, Shield, Search, Info } from 'lucide-react';

import { SectionTabs, SECTIONS } from '../components/enterprise';

const RACI_COLORS = {
  R: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Responsible' },
  A: { bg: 'bg-red-100', text: 'text-red-700', label: 'Accountable' },
  C: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Consulted' },
  I: { bg: 'bg-green-100', text: 'text-green-700', label: 'Informed' },
};

const RACIPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ entries: [], measures: [], contacts: [], users: [] });
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/raci');
      setData(res.data);
      const m = {};
      (res.data.entries || []).forEach(e => { m[e.measure_id] = e; });
      setMatrix(m);
    } catch { toast.error('Kon RACI-matrix niet laden'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allPeople = [...(data.users || []).map(u => ({ id: u.id, name: u.name, type: 'user' })), ...(data.contacts || []).map(c => ({ id: c.id, name: c.name, type: 'contact' }))];

  const setRACIRole = (measureId, role, personId) => {
    setMatrix(prev => {
      const entry = prev[measureId] || { responsible: [], accountable: [], consulted: [], informed: [] };
      const roleKey = role === 'R' ? 'responsible' : role === 'A' ? 'accountable' : role === 'C' ? 'consulted' : 'informed';
      const current = entry[roleKey] || [];
      const updated = current.includes(personId) ? current.filter(id => id !== personId) : [...current, personId];
      return { ...prev, [measureId]: { ...entry, [roleKey]: updated } };
    });
  };

  const saveEntry = async (measureId) => {
    setSaving(measureId);
    try {
      const entry = matrix[measureId] || {};
      await api.put(`/raci/${measureId}`, {
        responsible: entry.responsible || [],
        accountable: entry.accountable || [],
        consulted: entry.consulted || [],
        informed: entry.informed || [],
      });
      toast.success('RACI bijgewerkt');
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(null); }
  };

  const getPersonName = (id) => allPeople.find(p => p.id === id)?.name || id;

  const filteredMeasures = (data.measures || []).filter(m => {
    if (domainFilter !== 'all' && m.domain !== domainFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return m.name?.toLowerCase().includes(q) || m.measure_id?.toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <MainLayout><div className="min-h-screen bg-white p-10"><Skeleton className="h-8 w-64 mb-6" /><Skeleton className="h-96 rounded-xl" /></div></MainLayout>;

  return (
    <MainLayout>
      <div className="min-h-screen bg-white" data-testid="raci-page">
        <SectionTabs tabs={SECTIONS.organisatie.tabs} />
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-black">RACI-matrix</h1>
              <p className="text-sm text-neutral-500 mt-1">Wijs verantwoordelijkheden toe per maatregel</p>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-neutral-50 border border-neutral-200">
              {Object.entries(RACI_COLORS).map(([key, cfg]) => (
                <Badge key={key} className={`${cfg.bg} ${cfg.text} text-[10px]`}>{key} = {cfg.label}</Badge>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek maatregel..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black" data-testid="raci-search" />
            </div>
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle domeinen</SelectItem>
                <SelectItem value="people">Mens</SelectItem>
                <SelectItem value="process">Proces</SelectItem>
                <SelectItem value="technology">Technologie</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-[10px] text-neutral-400">{filteredMeasures.length} maatregelen</span>
          </div>

          {filteredMeasures.length === 0 ? (
            <div className="text-center py-16 text-neutral-400">
              <Shield className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Geen maatregelen gevonden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMeasures.map(measure => {
                const entry = matrix[measure.id] || {};
                const hasAssignments = (entry.responsible?.length || 0) + (entry.accountable?.length || 0) + (entry.consulted?.length || 0) + (entry.informed?.length || 0) > 0;
                return (
                  <div key={measure.id} className="rounded-xl border border-neutral-200 overflow-hidden" data-testid={`raci-row-${measure.id}`}>
                    <div className="flex items-center justify-between px-4 py-3 bg-neutral-50/50">
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge variant="outline" className="text-[10px] shrink-0">{measure.measure_id}</Badge>
                        <p className="text-sm font-medium text-black truncate">{measure.name}</p>
                        <Badge className={`text-[10px] ${measure.domain === 'people' ? 'bg-blue-50 text-blue-600' : measure.domain === 'process' ? 'bg-purple-50 text-purple-600' : 'bg-teal-50 text-teal-600'}`}>
                          {measure.domain === 'people' ? 'Mens' : measure.domain === 'process' ? 'Proces' : 'Technologie'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!hasAssignments && <span className="text-[10px] text-amber-500">Geen toewijzing</span>}
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => saveEntry(measure.id)} disabled={saving === measure.id} data-testid={`raci-save-${measure.id}`}>
                          <Save className="h-3 w-3 mr-1" /> {saving === measure.id ? '...' : 'Opslaan'}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-px bg-neutral-100">
                      {['R', 'A', 'C', 'I'].map(role => {
                        const roleKey = role === 'R' ? 'responsible' : role === 'A' ? 'accountable' : role === 'C' ? 'consulted' : 'informed';
                        const assigned = entry[roleKey] || [];
                        const cfg = RACI_COLORS[role];
                        return (
                          <div key={role} className="bg-white p-3">
                            <p className={`text-[10px] font-bold ${cfg.text} mb-2`}>{role}: {cfg.label}</p>
                            <div className="space-y-1 mb-2">
                              {assigned.map(pid => (
                                <div key={pid} className={`flex items-center justify-between px-2 py-1 rounded ${cfg.bg}`}>
                                  <span className="text-[10px] font-medium truncate">{getPersonName(pid)}</span>
                                  <button onClick={() => setRACIRole(measure.id, role, pid)} className="text-neutral-400 hover:text-red-500 ml-1">
                                    <span className="text-[10px]">x</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                            <Select onValueChange={v => { if (v && !assigned.includes(v)) setRACIRole(measure.id, role, v); }}>
                              <SelectTrigger className="h-7 text-[10px] border-dashed" data-testid={`raci-${role}-${measure.id}`}>
                                <SelectValue placeholder="+ Toevoegen" />
                              </SelectTrigger>
                              <SelectContent>
                                {allPeople.filter(p => !assigned.includes(p.id)).map(p => (
                                  <SelectItem key={p.id} value={p.id} className="text-xs">{p.name} ({p.type === 'user' ? 'Gebruiker' : 'Contact'})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default RACIPage;
